import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  CircularProgress,
} from "@material-ui/core";
import { MdWarning, MdBlock, MdClose, MdChevronRight } from "react-icons/md";

const REASON_MESSAGES = {
  next_trip_exists: "A Next Trip has already been created from this operation. Only one Next Trip can be created per operation.",
  trip_end: "The vessel contract for this trip has been ended. No further Next Trip can be created.",
  redelivery: "The vessel settlement mode is set to Redelivery (Trip Ended). Next Trip cannot be created for a redelivered vessel.",
  no_vessel_contract: "No vessel contract is associated with this operation.",
};

export const normalizeTripEligibilityResponse = (response = {}) => {
  const payload = response?.data && !response?.eligible && !response?.reason ? response.data : response;
  const eligible = Boolean(payload?.eligible);
  const reason = payload?.reason || "next_trip_blocked";

  return {
    eligible,
    reason,
  };
};

const TripEndWarningModal = ({
  open,
  onClose,
  mode = "blocked", // "blocked" | "confirm_end"
  reason = "",
  onConfirmEndTrip,
}) => {
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    if (!onConfirmEndTrip) return;
    setSubmitting(true);
    try {
      await onConfirmEndTrip();
      onClose();
    } catch (err) {
      console.error("Error ending trip:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const getReasonText = () => {
    return REASON_MESSAGES[reason] || reason || "Next Trip cannot be created for this operation.";
  };

  const impactPoints = [
    { text: "The trip will be marked as ", highlight: "completed" },
    { text: "No additional Next Trip can be created from this Vessel Contract" },
    { text: "The Vessel Contract will no longer be available in the Next Trip flow" },
    { text: "Existing trips, contracts, and downstream operations remain unchanged" },
    { text: "Affects only future trip creation from this Vessel Contract" },
  ];

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      classes={{
        paper: "rounded-none overflow-hidden border border-slate-200 shadow-2xl",
      }}
    >
      {/* Sleek Light Header Bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '6px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {mode === "blocked" ? (
            <MdBlock size={17} color="#ef4444" />
          ) : (
            <MdWarning size={17} color="#d97706" />
          )}
          <span style={{ color: '#0f172a', fontSize: 13, fontWeight: 600, letterSpacing: '0.01em' }}>
            {mode === "blocked" ? "Next Trip Blocked" : "Confirm Trip End"}
          </span>
        </div>
        <button
          onClick={onClose}
          disabled={submitting}
          style={{ padding: 2, color: '#64748b', background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          title="Close"
        >
          <MdClose size={16} />
        </button>
      </div>

      {/* Styled Dialog Content */}
      <DialogContent style={{ padding: '12px 14px', background: '#fff' }}>
        {mode === "blocked" ? (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderLeft: '3px solid #ef4444', padding: '8px 10px', fontSize: 12, color: '#991b1b', lineHeight: 1.45 }}>
            {getReasonText()}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {/* Primary Alert Callout */}
            <div style={{ background: '#fefce8', border: '1px solid #fef08a', borderLeft: '3px solid #eab308', padding: '8px 10px' }}>
              <p style={{ margin: 0, fontSize: 12, color: '#854d0e', lineHeight: 1.4, fontWeight: 500 }}>
                Ending this trip will mark the current Vessel Contract as <strong style={{ color: '#713f12', fontWeight: 600 }}>Trip Ended</strong>.
              </p>
            </div>

            {/* Impact Points List */}
            <div>
              <span style={{ display: 'block', fontSize: 10, fontWeight: 600, color: '#475569', letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 6 }}>
                Impact & Restrictions
              </span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {impactPoints.map((item, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 5 }}>
                    <MdChevronRight size={14} color="#64748b" style={{ flexShrink: 0, marginTop: 1 }} />
                    <span style={{ fontSize: 11, color: '#334155', lineHeight: 1.4 }}>
                      {item.text}
                      {item.highlight && <strong style={{ color: '#0f172a', fontWeight: 600 }}>{item.highlight}</strong>}.
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </DialogContent>

      {/* Compact Action Footer */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, padding: '6px 12px', background: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
        {mode === "blocked" ? (
          <button
            onClick={onClose}
            style={{ height: 28, padding: '0 14px', fontSize: 11, fontWeight: 500, color: '#fff', background: '#313C4C', border: '1px solid #313C4C', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.05em' }}
          >
            Got It
          </button>
        ) : (
          <>
            <button
              onClick={onClose}
              disabled={submitting}
              style={{ height: 28, padding: '0 14px', fontSize: 11, fontWeight: 500, color: '#475569', background: '#fff', border: '1px solid #cbd5e1', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.05em', opacity: submitting ? 0.5 : 1 }}
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={submitting}
              style={{ height: 28, padding: '0 14px', fontSize: 11, fontWeight: 600, color: '#fff', background: '#dc2626', border: '1px solid #dc2626', cursor: submitting ? 'not-allowed' : 'pointer', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: 6, opacity: submitting ? 0.5 : 1 }}
            >
              {submitting ? (
                <>
                  <CircularProgress size={12} color="inherit" />
                  <span>Ending Trip...</span>
                </>
              ) : (
                "End Trip"
              )}
            </button>
          </>
        )}
      </div>
    </Dialog>
  );
};

export default TripEndWarningModal;
